import onetime from "@-/com.npmjs.onetime";
import { useSyncExternalStore } from "react";
import { BoundedBuffer } from "#bounded-buffer";
import { subscribeToCapturedLogs } from "#subscribe-to-captured-logs";
import type { DiagnosticsLogEntry } from "#diagnostics-types";

export type RecentDebugLogEntry = DiagnosticsLogEntry & { id: number };

const getState = onetime(() => ({
  buffer: new BoundedBuffer<RecentDebugLogEntry>(500, 500_000),
  listeners: new Set<() => void>(),
  nextId: 0,
  snapshot: [] as readonly RecentDebugLogEntry[],
  timer: null as ReturnType<typeof setTimeout> | null,
}));

function publish(): void {
  const state = getState();
  state.snapshot = state.buffer.toArray();
  for (const listener of state.listeners) {
    listener();
  }
}

/** Mounted once at the root for eligible users, before the panel is opened. */
export function startRecentDebugLogs(): () => void {
  const state = getState();
  const unsubscribe = subscribeToCapturedLogs((entry) => {
    state.buffer.push({ ...entry, id: state.nextId++ }, JSON.stringify(entry).length);
    // A log storm should not turn the log viewer into a render storm.
    if (state.timer === null) {
      state.timer = setTimeout(() => {
        state.timer = null;
        publish();
      }, 500);
    }
  });
  return () => {
    unsubscribe();
    if (state.timer !== null) {
      clearTimeout(state.timer);
      state.timer = null;
    }
    // Signing out of an admin account must not expose that account's logs later.
    state.buffer.clear();
    publish();
  };
}

function subscribe(listener: () => void): () => void {
  getState().listeners.add(listener);
  return () => {
    getState().listeners.delete(listener);
  };
}

function getSnapshot(): readonly RecentDebugLogEntry[] {
  return getState().snapshot;
}

export function useRecentDebugLogs(): readonly RecentDebugLogEntry[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function formatDebugLog(entry: DiagnosticsLogEntry): string {
  const error = entry.error === null ? "" : `\n${entry.error}`;
  return `${new Date(entry.timestamp).toISOString()} [${entry.level.toUpperCase()}] [${entry.source}] ${entry.message}${error}`;
}

/** Read the buffer at export time, including lines not yet published to the UI. */
export function getRecentDebugLogsText(): string {
  return getState()
    .buffer.toArray()
    .map((entry) => formatDebugLog(entry))
    .join("\n\n");
}
